import type { DiscoverySuggestionsResponseType } from "@sellar-io/api-v2/types";
import { parseMarketFilterQuery } from "./parseMarketFilterQuery";
import {
  localMarketSuggestions,
  marketCategoryShortcuts,
  type MarketSearchSelection,
} from "./marketSearchSelections";
export type MarketSearchGroup = {
  id: string;
  kind: "suggestions" | "keyword";
  label: string;
  options: MarketSearchSelection[];
  grid?: boolean;
};
export const buildMarketSearchGroups = ({
  search,
  scope,
  history,
  data,
  isLoading,
  isError,
}: {
  search: string;
  scope: "market" | "suppliers";
  history: MarketSearchSelection[];
  data: DiscoverySuggestionsResponseType | undefined;
  isLoading: boolean;
  isError: boolean;
}) => {
  const noSupplierMatches =
    scope === "suppliers" &&
    search.length >= 1 &&
    !isLoading &&
    !isError &&
    !data?.suppliers.length;
  const groups: MarketSearchGroup[] = [];
  if (scope === "suppliers") {
    groups.push({
      id: "suppliers",
      kind: "suggestions",
      label: "My suppliers",
      options: (data?.suppliers ?? []).map((supplier) => ({
        kind: "supplier",
        supplierId: supplier.id,
        label: supplier.name,
        location: supplier.location,
        logoUrl: supplier.logoUrl,
      })),
    });
  } else if (!search) {
    groups.push({
      id: "history",
      kind: "suggestions",
      label: "Recent searches",
      options: history,
    });
    groups.push({
      id: "categories",
      kind: "suggestions",
      label: "Categories",
      options: marketCategoryShortcuts,
      grid: true,
    });
  } else {
    const filters = localMarketSuggestions
      .filter((selection) =>
        selection.label
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase()),
      )
      .slice(0, 6);
    groups.push({
      id: "filters",
      kind: "suggestions",
      label: "Filters",
      options: filters,
    });
    groups.push({
      id: "suppliers",
      kind: "suggestions",
      label: "Suppliers",
      options: (data?.suppliers ?? []).map((supplier) => ({
        kind: "supplier",
        supplierId: supplier.id,
        label: supplier.name,
        logoUrl: supplier.logoUrl,
      })),
    });
    groups.push({
      id: "products",
      kind: "suggestions",
      label: "Products",
      options: (data?.isFallback ? [] : (data?.products ?? [])).map(
        (product) => ({
          kind: "product",
          parentProductId: product.parentProductId,
          productItemId: product.productItemId,
          imageUrl: product.imageUrl,
          supplierId: product.supplierId,
          supplierName: product.supplierName,
          label: product.name,
        }),
      ),
    });
  }
  if (search && (scope === "market" || noSupplierMatches || isError)) {
    const filterQuery = parseMarketFilterQuery(search);
    if (filterQuery) {
      groups.unshift({
        id: "filter-query",
        kind: "suggestions",
        label: "Filters",
        options: [filterQuery],
      });
    } else {
      groups.push({
        id: "keyword",
        kind: "keyword",
        label: scope === "market" ? "Keyword search" : "Search all of Sellar",
        options: [
          { kind: "text", value: search, label: search, scope: "market" },
        ],
      });
    }
  }

  return { groups, noSupplierMatches };
};
